// Demo-guide data schemas.
//
// Unlike onboarding.state this is persisted in localStorage rather than
// pref-storage: the audience is unauthenticated, so there is no token to
// PATCH /preferences/user/{key} with. Losing it is cheap — the worst case is
// a returning visitor being asked which asset class they trade a second time.

import type { AssetClassId } from "@/onboarding/types";
import type { DemoStepId } from "./flows";

export type { AssetClassId };

export type DemoGuideState = {
  version: 1;
  /** Written when the picker is first shown — makes triggering idempotent. */
  startedAt: string;
  /** Set when an asset class is picked, or when the picker is declined. */
  pickerAnsweredAt?: string;
  /** The asset class they chose; selects the template and the watchlist recording. */
  assetClass?: AssetClassId;
  /** Id of the applied demo template, for the funnel. */
  templateId?: string;
  /** Index of the guided step currently being asked for. */
  step: number;
  /**
   * Steps actually completed, in the order they were done.
   *
   * Skipped steps are absent, which is what makes `steps_completed` on
   * `flow_complete` mean something. It also makes `flow_step` idempotent
   * without a session-scoped set: a step already in here has been reported,
   * and reporting it twice would be a step counted twice.
   *
   * Optional because state persisted by an earlier build won't carry it.
   */
  completedStepIds?: DemoStepId[];
  /** Set when the last step completes. */
  completedAt?: string;
  /**
   * Set when they "create" the watchlist in the builder.
   *
   * The step does not end here — they still have to get back to the dashboard
   * and apply it — but the instruction and the glowing nav item both change,
   * because at this point the thing to do is somewhere else.
   */
  watchlistCreatedAt?: string;
  /** Set if they close the rail without finishing. */
  exitedAt?: string;
  /**
   * Popping a widget out is recorded whether or not it was the step being
   * asked for, so the checklist entry stays reachable from any path.
   */
  poppedOutAt?: string;
  checklistDismissedAt?: string;
};

export const DEMO_GUIDE_STATE_KEY = "demo.guide.state";

export function createInitialDemoGuideState(): DemoGuideState {
  return {
    version: 1,
    startedAt: new Date().toISOString(),
    step: 0,
    completedStepIds: [],
  };
}
